import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, getDocs, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { IoCopyOutline, IoSettingsOutline, IoClose, IoPersonOutline } from 'react-icons/io5';
import AvatarViewer from '../Shared/AvatarViewer';

const GestionInquilinosCard = ({ currentUser, userData, codigoCondominio }) => {
    const [inquilinos, setInquilinos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInquilino, setSelectedInquilino] = useState(null);
    const [savingPermisos, setSavingPermisos] = useState(false);

    useEffect(() => {
        if (!currentUser?.uid || userData?.tipoResidente !== 'Propietario') {
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, "usuarios"),
            where("cuentaPadreId", "==", currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                permisosInquilino: doc.data().permisosInquilino || {
                    accesos: false,
                    reservar: false,
                    vehiculos: false,
                    estacionamientos: false,
                    cuotas: false,
                    finanzas: false
                }
            }));
            setInquilinos(list);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser, userData]);

    const handleCopyLink = () => {
        if (!codigoCondominio) {
            alert("El Condominio no tiene un código de activación válido.");
            return;
        }
        const appUrl = window.location.origin;
        const link = `${appUrl}/registro?codigo=${codigoCondominio}&invita=${currentUser.uid}`;
        navigator.clipboard.writeText(link).then(() => {
            alert("Enlace de invitación copiado al portapapeles.");
        });
    };

    const handleWhatsAppShare = () => {
        if (!codigoCondominio) {
            alert("El Condominio no tiene un código de activación válido.");
            return;
        }
        const appUrl = window.location.origin;
        const link = `${appUrl}/registro?codigo=${codigoCondominio}&invita=${currentUser.uid}`;
        const mensaje = encodeURIComponent(`¡Hola! Únete a la app de nuestro condominio para tener acceso. Descárgala o regístrate aquí: ${link}`);
        window.open(`https://wa.me/?text=${mensaje}`, '_blank');
    };

    const togglePermiso = async (permisoKey) => {
        if (!selectedInquilino) return;
        
        const newValue = !selectedInquilino.permisosInquilino[permisoKey];
        const updatedPermisos = {
            ...selectedInquilino.permisosInquilino,
            [permisoKey]: newValue
        };

        // Update local state immediately for snappy UI
        setSelectedInquilino({
            ...selectedInquilino,
            permisosInquilino: updatedPermisos
        });

        // Update Backend
        setSavingPermisos(true);
        try {
            const ref = doc(db, "usuarios", selectedInquilino.id);
            await updateDoc(ref, {
                permisosInquilino: updatedPermisos
            });
        } catch (e) {
            console.error("Error updating permisos", e);
            alert("Error al guardar permisos.");
            // Revert on fail
            setSelectedInquilino({
                ...selectedInquilino,
                permisosInquilino: {
                    ...selectedInquilino.permisosInquilino,
                    [permisoKey]: !newValue
                }
            });
        } finally {
            setSavingPermisos(false);
        }
    };

    const handleAprobarInquilino = async () => {
        if (!selectedInquilino) return;
        setSavingPermisos(true);
        try {
            const ref = doc(db, "usuarios", selectedInquilino.id);
            await updateDoc(ref, {
                estatusValidacion: 'aprobado',
                numeroCasa: userData.numeroCasa || '',
                tipoResidente: 'Inquilino'
            });
            // Update local state to reflect UI change
            setSelectedInquilino({
                ...selectedInquilino,
                estatusValidacion: 'aprobado',
                numeroCasa: userData.numeroCasa || '',
                tipoResidente: 'Inquilino'
            });
        } catch (e) {
            console.error("Error aprobando inquilino", e);
            alert("Error al aprobar inquilino.");
        } finally {
            setSavingPermisos(false);
        }
    };

    if (userData?.tipoResidente !== 'Propietario') return null;

    return (
        <div style={{ marginBottom: '24px' }}>
            <div className="profile-section-title">
                <span>Mis Inquilinos / Familia</span>
                <button type="button" onClick={handleCopyLink} className="profile-section-edit" style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#e0e7ff', padding: '6px 12px', borderRadius: '14px', color: '#6366f1' }}>
                    <IoCopyOutline size={16}/> Invitar
                </button>
            </div>

            <div className="profile-cards-group">
                {loading ? (
                    <p style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', margin: 0, fontSize: '0.9rem' }}>Cargando inquilinos...</p>
                ) : inquilinos.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '25px 20px' }}>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Aún no tienes cuentas subordinadas vinculadas a tu unidad.</p>
                    </div>
                ) : (
                    inquilinos.map(inq => (
                        <div key={inq.id} className="profile-data-card" style={{ flexDirection: 'row', alignItems: 'center', padding: '16px 20px' }}>
                            <div style={{ width: '42px', height: '42px', borderRadius: '50%', overflow: 'hidden', marginRight: '16px', flexShrink: 0 }}>
                                <AvatarViewer avatarUrl={inq.fotoPerfilUrl} size={18} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <strong style={{ display: 'block', color: '#1e293b', fontSize: '0.95rem', marginBottom: '2px' }}>{inq.nombre}</strong>
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    {inq.tipoResidente || 'Dependiente'} • <span style={{ color: inq.estatusValidacion === 'pendiente' ? '#d97706' : '#16a34a' }}>{inq.estatusValidacion === 'pendiente' ? 'Pendiente' : 'Activo'}</span>
                                </span>
                            </div>
                            <button 
                                type="button"
                                onClick={() => setSelectedInquilino(inq)}
                                style={{ background: 'transparent', border: 'none', color: '#0f172a', padding: '8px', cursor: 'pointer', display: 'flex' }}
                            >
                                <IoSettingsOutline size={22} />
                            </button>
                        </div>
                    ))
                )}
            </div>

            {/* MODAL DE PERMISOS */}
            {selectedInquilino && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', overflow: 'hidden' }} onClick={() => setSelectedInquilino(null)}>
                    <div style={{ background: 'white', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '25px', paddingBottom: '110px', animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>Permisos para {selectedInquilino.nombre.split(' ')[0]}</h3>
                            <button type="button" onClick={() => setSelectedInquilino(null)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><IoClose size={24} /></button>
                        </div>

                        {selectedInquilino.estatusValidacion === 'pendiente' && (
                            <div style={{ background: '#fef3c7', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #fde68a' }}>
                                <p style={{ margin: '0 0 10px 0', color: '#92400e', fontSize: '0.9rem' }}>
                                    <strong>Atención:</strong> Esta cuenta no tiene acceso a la App porque aún no ha sido validada.
                                </p>
                                <button 
                                    onClick={handleAprobarInquilino}
                                    disabled={savingPermisos}
                                    style={{ width: '100%', background: '#f59e0b', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', opacity: savingPermisos ? 0.7 : 1 }}
                                >
                                    Aprobar y Activar Cuenta
                                </button>
                            </div>
                        )}

                        <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '20px' }}>
                            Los módulos de Muro, Avisos, Marketplace, Encuestas, Seguridad y Botón de Pánico <strong>siempre están activos</strong> por defecto. Decide si deseas darle acceso a los siguientes módulos clave:
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {[
                                { key: 'accesos', label: 'Invitaciones y Accesos (Visitas/Proveedores)' },
                                { key: 'reservar', label: 'Reservación de Amenidades' },
                                { key: 'vehiculos', label: 'Gestión de Vehículos' },
                                { key: 'estacionamientos', label: 'Estacionamiento de Visitas' },
                                { key: 'cuotas', label: 'Pagar Cuotas del Condominio' },
                                { key: 'finanzas', label: 'Ver Finanzas del Condominio' }
                            ].map(permiso => (
                                <div key={permiso.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                                    <span style={{ color: '#334155', fontWeight: '500', fontSize: '0.95rem' }}>{permiso.label}</span>
                                    
                                    {/* Toggle Switch UI */}
                                    <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={!!selectedInquilino.permisosInquilino[permiso.key]}
                                            onChange={() => togglePermiso(permiso.key)}
                                            disabled={savingPermisos}
                                            style={{ opacity: 0, width: 0, height: 0 }} 
                                        />
                                        <span style={{ 
                                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, 
                                            backgroundColor: selectedInquilino.permisosInquilino[permiso.key] ? '#10b981' : '#cbd5e1', 
                                            transition: '.4s', borderRadius: '34px',
                                            opacity: savingPermisos ? 0.6 : 1
                                        }}>
                                            <span style={{ 
                                                position: 'absolute', content: '""', height: '20px', width: '20px', left: '3px', bottom: '3px', 
                                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                                transform: selectedInquilino.permisosInquilino[permiso.key] ? 'translateX(22px)' : 'translateX(0)' 
                                            }} />
                                        </span>
                                    </label>

                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            )}
            
            <style>{`
            @keyframes slideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
            }
            `}</style>
        </div>
    );
};

export default GestionInquilinosCard;
